<template>
  <aside class="sidebar">
    <label class="mobile-navigation">
      <span>Ceremony navigation</span>
      <select :value="currentPath" @change="navigate">
        <optgroup v-for="group in groups" :key="group.label" :label="group.label">
          <option v-for="item in group.items" :key="item.path" :value="item.path">{{ item.label }}{{ item.newTab ? ' (new tab)' : '' }}</option>
        </optgroup>
      </select>
    </label>
    <nav class="sidebar-inner" aria-label="Ceremony sections">
      <router-link to="/" class="sidebar-home">← All events</router-link>
      <template v-for="group in desktopGroups" :key="group.label">
        <details v-if="group.collapsible" class="nav-disclosure" :open="containsCurrentPage(group)">
          <summary :class="{ 'contains-current': containsCurrentPage(group) }">{{ group.label }}</summary>
          <div class="disclosure-links">
            <router-link v-for="item in group.items" :key="item.path" :to="item.path"
              :target="item.newTab ? '_blank' : undefined" :rel="item.newTab ? 'noopener noreferrer' : undefined"
              :title="item.newTab ? 'Opens in a new tab' : undefined"
              class="sidebar-link" :class="{ active: currentPath === item.path }"
              :aria-current="currentPath === item.path ? 'page' : undefined">{{ item.label }}</router-link>
          </div>
        </details>
        <div v-else class="nav-group" :class="{ 'ceremony-link': group.label === 'Ceremony day' }">
          <router-link v-for="item in group.items" :key="item.path" :to="item.path"
              :target="item.newTab ? '_blank' : undefined" :rel="item.newTab ? 'noopener noreferrer' : undefined"
              :title="item.newTab ? 'Opens in a new tab' : undefined"
            class="sidebar-link" :class="{ active: currentPath === item.path || (item.page === 'overview' && isFinalCheck) }"
            :aria-current="currentPath === item.path ? 'page' : undefined">{{ item.label }}</router-link>
          <router-link v-if="group.label === 'Preparation' && isFinalCheck" :to="currentPath"
            class="sidebar-link current-step" aria-current="page">Final check</router-link>
        </div>
      </template>
    </nav>
  </aside>
</template>

<script>
import { canManageMessages, hasAnyRole } from '@/lib/auth';
export default {
  name: 'EventSidebar',
  props: { eventId: { type: String, required: true } },
  methods: {
    containsCurrentPage(group) {
      return group.items.some(item => item.path === this.currentPath);
    },
    async navigate(event) {
      const select = event.target;
      const item = this.groups.flatMap(group => group.items).find(item => item.path === select.value);
      if (item?.newTab) {
        window.open(this.$router.resolve(item.path).href, '_blank', 'noopener,noreferrer');
      } else {
        await this.$router.push(select.value);
      }
      select.value = this.currentPath;
    },
  },
  computed: {
    currentPath() {
      if (this.$route.path === '/manage/messages') return `/manage/messages?eventId=${encodeURIComponent(this.eventId)}`;
      return this.$route.path.replace(/\/manage$/, '/queue');
    },
    isFinalCheck() { return this.$route.path.endsWith('/ready'); },
    desktopGroups() {
      return this.groups.map(group => ({
        ...group,
        items: group.items.filter(item => item.page !== 'ready'),
      })).filter(group => group.items.length);
    },
    groups() {
      const admin = ['internal_admin', 'uni_admin'];
      const operate = [...admin, 'operator'];
      const path = page => `/events/${encodeURIComponent(this.eventId)}/${page}`;
      const item = (label, page, roles) => ({ label, page, path: path(page), newTab: ['presenter', 'lower-thirds'].includes(page), visible: !roles || hasAnyRole(roles) });
      return [
        { label: 'Preparation', items: [
          item('Overview', 'overview'),
          item('Graduate list', 'participants', operate),
          item('Student responses', 'review', admin),
          item('Orator preparation', 'orator-prep', [...admin, 'orator']),
          item('Final check', 'ready', admin),
        ] },
        { label: 'Ceremony day', items: [
          item('Check-in', 'check-in', operate),
          item('Run ceremony', 'queue', operate),
        ] },
        { label: 'Screens & devices', collapsible: hasAnyRole(operate), items: [
          item('Presenter', 'presenter', [...operate, 'orator']),
          item('Lower thirds', 'lower-thirds', [...operate, 'av']),
          item('Devices', 'devices', operate),
        ] },
        { label: 'Tools & settings', collapsible: true, items: [
          { label: 'Messages', path: `/manage/messages?eventId=${encodeURIComponent(this.eventId)}`, visible: canManageMessages() },
          item('Downloads', 'exports', admin),
          item('Screen appearance', 'views', [...admin, 'av']),
          item('Setup & import', 'setup', admin),
        ] },
      ].map(group => ({ ...group, items: group.items.filter(entry => entry.visible) })).filter(group => group.items.length);
    },
  },
};
</script>

<style scoped>
.sidebar { width: 190px; flex: 0 0 190px; }
.sidebar-inner { padding: 0.25rem 0.4rem; }
.sidebar-home { display: block; padding: 0.6rem; margin-bottom: 1.25rem; font-size: 0.85rem; color: var(--orator-graphite); }
.nav-group { margin-bottom: 0.75rem; }
.ceremony-link { border-top: 1px solid var(--orator-smoke); padding-top: 0.75rem; margin-top: 1.25rem; }
.nav-disclosure { margin-top: 0.35rem; }
.nav-disclosure summary { padding: 0.7rem 0.6rem; cursor: pointer; font-size: 0.85rem; color: var(--orator-graphite); min-height: 44px; }
.nav-disclosure summary:hover, .nav-disclosure summary.contains-current { color: var(--orator-navy); }
.disclosure-links { margin: 0.15rem 0 0.75rem 0.7rem; padding-left: 0.35rem; border-left: 1px solid var(--orator-smoke); }
.disclosure-links .sidebar-link { font-size: 0.85rem; font-weight: 500; }
.sidebar-link.current-step { margin-left: 0.6rem; padding-left: 0.75rem; border-left: 2px solid var(--orator-blue); border-radius: 0; font-size: 0.8rem; }
.sidebar a:focus-visible, .sidebar summary:focus-visible, .sidebar select:focus-visible { outline: 2px solid var(--orator-blue); outline-offset: 3px; }
.sidebar-link { display: block; padding: 0.6rem; margin: 0.15rem 0; border-radius: 0.4rem; font-size: 0.9rem; font-weight: 600; color: var(--orator-arsenic); text-decoration: none; }
.sidebar-link.active { background: var(--orator-cloud); color: var(--orator-navy); }
.sidebar-link:not(.active):hover { background: var(--orator-cloud); }
.mobile-navigation { display: none; }
@media (max-width: 960px) {
  .sidebar { width: 100%; flex-basis: auto; }
  .sidebar-inner { display: none; }
  .mobile-navigation { display: grid; gap: 0.3rem; color: var(--orator-graphite); font-size: 0.75rem; }
  .mobile-navigation select { width: 100%; min-height: 44px; background: white; border: 1px solid var(--orator-steel); border-radius: 0.4rem; color: var(--orator-navy); font-size: 1rem; }
}
</style>
